<script setup>
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";
import { ChevronDownIcon } from "@heroicons/vue/24/solid";
import { ErrorMessage, Field } from "vee-validate";
import { computed, onMounted } from "vue";

const {
  fieldId,
  fieldName,
  label,
  options,
  optionKey,
  disabled = false,
  required = false,
  multiple = false,
  hasEmptyOption = false,
  inlineLabel = false,
  size = "medium",
  by = null,
  legend = null,
} = defineProps({
  fieldId: {
    type: String,
    required: true,
  },
  fieldName: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  options: {
    type: Array,
    required: true,
  },
  optionKey: {
    type: String,
    required: false,
  },
  disabled: {
    type: Boolean,
    required: false,
  },
  required: {
    type: Boolean,
    required: false,
  },
  multiple: {
    type: Boolean,
    required: false,
  },
  hasEmptyOption: {
    type: Boolean,
    required: false,
  },
  inlineLabel: {
    type: Boolean,
    required: false,
  },
  size: {
    type: String,
    required: false,
  },
  by: {
    type: String,
    required: false,
  },
  legend: {
    type: String,
    required: false,
  },
});

const model = defineModel();

const computedProps = computed(() => {
  let tmpProps = {};

  if (by) {
    tmpProps.by = by;
  }

  return tmpProps;
});

onMounted(() => {
  if (multiple && model.value === null) {
    model.value = [];
  }
});
</script>

<template>
  <div
    class="mb-4 flex"
    :class="{
      'flex-row items-center': inlineLabel,
      'flex-col': !inlineLabel,
    }"
  >
    <label
      :for="fieldName"
      class="whitespace-nowrap"
      :class="{
        'mr-2': inlineLabel,
        'mb-2': !inlineLabel,
        'text-sm': size === 'small',
      }"
    >
      {{ label }}
      <sup v-if="required" class="text-red-500">*</sup>
    </label>

    <Field :id="fieldId" :name="fieldName" v-model="model" :as="Listbox">
      <div class="relative">
        <Listbox
          v-slot="{ open }"
          v-model="model"
          :disabled="disabled"
          :multiple="multiple"
          v-bind="computedProps"
        >
          <ListboxButton
            class="flex w-full items-center justify-between rounded border border-gray-300 text-left ring-1 duration-150 focus:outline-0 focus:ring-gray-300 disabled:cursor-not-allowed disabled:bg-gray-100"
            :class="{
              '!ring-gray-300': open,
              'ring-transparent': !open,
              'min-w-[210px] px-5 py-3': size === 'medium',
              'h-[28px] min-w-[140px] px-3 py-1 text-sm': size === 'small',
            }"
            :disabled="disabled"
          >
            <template v-if="model">
              <slot name="buttonContent" />
            </template>
            <template v-else> &nbsp;</template>

            <ChevronDownIcon
              class="ml-2 size-4 text-gray-500 duration-150"
              :class="{
                'rotate-180 transform': open,
              }"
            />
          </ListboxButton>

          <ListboxOptions
            class="absolute left-0 z-50 max-h-60 w-full overflow-y-auto rounded border border-gray-300 bg-white shadow focus:outline-0"
            :class="{
              'top-14': size === 'medium',
              'top-8': size === 'small',
            }"
          >
            <ListboxOption
              v-if="hasEmptyOption && !multiple"
              v-slot="{ selected, active }"
              :value="null"
              as="template"
            >
              <li
                class="flex cursor-pointer items-center duration-150 hover:bg-indigo-600"
                :class="{
                  'bg-indigo-600 text-white': active || selected,
                  'h-[50px] px-5 py-2': size === 'medium',
                  'h-[28px] px-3 py-1 text-sm': size === 'small',
                }"
              >
                &nbsp;
              </li>
            </ListboxOption>

            <ListboxOption
              v-slot="{ selected, active }"
              v-for="(option, index) in options"
              :key="index"
              :value="optionKey ? option[optionKey] : option"
              as="template"
            >
              <li
                class="flex cursor-pointer items-center duration-150 hover:bg-indigo-600 hover:text-white"
                :class="{
                  'bg-indigo-600 text-white': active || selected,
                  'h-[40px] px-5 py-2': size === 'medium',
                  'h-[28px] px-3 py-1 text-sm': size === 'small',
                }"
              >
                <slot name="optionContent" :option="option" />
              </li>
            </ListboxOption>
          </ListboxOptions>
        </Listbox>
      </div>
    </Field>

    <em v-if="legend" class="mt-1 text-xs text-gray-400">
      {{ legend }}
    </em>

    <ErrorMessage :name="fieldName" class="mt-1 text-sm text-red-500" />
  </div>
</template>
